import {useCallback, useRef, useState} from 'react';
import type {FocusEvent, MouseEvent} from 'react';

import {CheckmarkButton} from '../../button/checkmark-button/checkmark-button';
import {CloseButton} from '../../button/close-button/close-button';
import {Stack} from '../../layout/stack/stack';
import {TextField} from '../../textfield/textfield';

import type {AddressValue} from './types';

interface Props {
    addressValue: AddressValue | null | undefined;
    isEditing: boolean;
    setIsEditing: (isEditing: boolean) => void;
    updateAddress: (addressValue: AddressValue) => Promise<any>;
    preventSaveOnBlur?: boolean;
    ignorePasswordAutoFill?: boolean;
}

export function AddressRowEditState({
    addressValue,
    isEditing,
    setIsEditing,
    updateAddress,
    preventSaveOnBlur,
    ignorePasswordAutoFill,
}: Props) {
    const initialAddressValueRef = useRef<AddressValue>({
        address1: addressValue?.address1 || '',
        address2: addressValue?.address2 || '',
        address3: addressValue?.address3 || '',
        city: addressValue?.city || '',
        state: addressValue?.state || '',
        postalCode: addressValue?.postalCode || '',
        country: addressValue?.country || '',
    });

    const [address1, setAddress1] = useState<string>(initialAddressValueRef.current.address1);
    const [address2, setAddress2] = useState<string>(initialAddressValueRef.current.address2);
    const [address3, setAddress3] = useState<string>(initialAddressValueRef.current.address3);
    const [city, setCity] = useState<string>(initialAddressValueRef.current.city);
    const [state, setState] = useState<string>(initialAddressValueRef.current.state);
    const [postalCode, setPostalCode] = useState<string>(initialAddressValueRef.current.postalCode);
    const [country, setCountry] = useState<string>(initialAddressValueRef.current.country);

    const saveAndClose = useCallback(() => {
        const initialAddress1Value = initialAddressValueRef.current.address1;
        const initialAddress2Value = initialAddressValueRef.current.address2;
        const initialAddress3Value = initialAddressValueRef.current.address3;
        const initialCityValue = initialAddressValueRef.current.city;
        const initialStateValue = initialAddressValueRef.current.state;
        const initialPostalCodeValue = initialAddressValueRef.current.postalCode;
        const initialCountryValue = initialAddressValueRef.current.country;

        if (
            address1.trim() !== initialAddress1Value.trim() ||
            address2.trim() !== initialAddress2Value.trim() ||
            address3.trim() !== initialAddress3Value.trim() ||
            city.trim() !== initialCityValue.trim() ||
            state.trim() !== initialStateValue.trim() ||
            postalCode.trim() !== initialPostalCodeValue.trim() ||
            country.trim() !== initialCountryValue.trim()
        ) {
            updateAddress({
                address1,
                address2,
                address3,
                city,
                state,
                postalCode,
                country,
            });
        } else {
            setIsEditing(false);
        }
    }, [
        address1,
        address2,
        address3,
        city,
        country,
        postalCode,
        setIsEditing,
        state,
        updateAddress,
    ]);

    const handleBlur = (event: FocusEvent<HTMLElement>) => {
        const related: any = event.relatedTarget;
        if (related && event.currentTarget && event.currentTarget.contains(related)) {
            return;
        }
        saveAndClose();
    };

    if (!isEditing) {
        return null;
    }

    return (
        <div onBlur={preventSaveOnBlur ? undefined : handleBlur}>
            <Stack spacing={2} isHorizontal={true}>
                <Stack spacing={2} isFullWidth={true}>
                    <TextField
                        size='small'
                        value={address1}
                        onChange={setAddress1}
                        // @ts-expect-error
                        autoFocus={true}
                        onEnter={saveAndClose}
                        placeholder='Address 1'
                        ignorePasswordAutoFill={ignorePasswordAutoFill}
                    />
                    <TextField
                        size='small'
                        value={address2}
                        onChange={setAddress2}
                        onEnter={saveAndClose}
                        placeholder='Address 2'
                        ignorePasswordAutoFill={ignorePasswordAutoFill}
                    />
                    <TextField
                        size='small'
                        value={address3}
                        onChange={setAddress3}
                        onEnter={saveAndClose}
                        placeholder='Address 3'
                        ignorePasswordAutoFill={ignorePasswordAutoFill}
                    />
                    <TextField
                        size='small'
                        value={city}
                        onChange={setCity}
                        onEnter={saveAndClose}
                        placeholder='City'
                        ignorePasswordAutoFill={ignorePasswordAutoFill}
                    />
                    <TextField
                        size='small'
                        value={state}
                        onChange={setState}
                        onEnter={saveAndClose}
                        placeholder='State'
                        ignorePasswordAutoFill={ignorePasswordAutoFill}
                    />
                    <TextField
                        size='small'
                        value={postalCode}
                        onChange={setPostalCode}
                        onEnter={saveAndClose}
                        placeholder='Postal code'
                        ignorePasswordAutoFill={ignorePasswordAutoFill}
                    />
                    <TextField
                        size='small'
                        value={country}
                        onChange={setCountry}
                        onEnter={saveAndClose}
                        placeholder='Country'
                        ignorePasswordAutoFill={ignorePasswordAutoFill}
                    />
                </Stack>
                <Stack alignItems='center' spacing={2}>
                    <CheckmarkButton
                        className='no-padding'
                        onClick={(e: MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            saveAndClose();
                        }}
                        // @ts-expect-error
                        variant='dark'
                    />
                    <CloseButton
                        size={12}
                        variant='dark'
                        onPointerDown={() => setIsEditing(false)}
                    />
                </Stack>
            </Stack>
        </div>
    );
}
